import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { BooleanFilter, buildPath, nameof } from '@e-socna/utils';
import { getFragmentData, ThirdPartyTypeEnum } from '@e-socna/data-layer';

import {
  PATH_BUSINESS_EVENT_DEFAULT,
  PATH_FOLDER_DEFAULT,
  PATH_MISSION_DEFAULT,
} from '../../app/constants/paths.constant';

import { AddressCardCmp_Address_Fragment } from '../Addresses/Card';

import { FolderBusinessEntityListCmp_ThirdParty_Fragment } from './fragments/third-party.fragment';
import type { FolderBusinessEntityListCmp_FolderBusinessEntity } from './fragments/folder-business-entity.fragment';
import type { FolderBusinessEntityState } from './types/folder-business-entity-state.type';
import { buildMissionPath } from '../../app/utils/mission-space.util';

interface Props {
  readonly folderBusinessEntity: FolderBusinessEntityListCmp_FolderBusinessEntity;
}

const Action = ({ folderBusinessEntity }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const typeName = folderBusinessEntity.__typename;

    let path: string;

    switch (typeName) {
      case 'Folder':
        path = buildPath(PATH_FOLDER_DEFAULT, {
          folderId: folderBusinessEntity._id,
        });
        break;
      case 'Mission': {
        path = buildMissionPath(PATH_MISSION_DEFAULT, {
          folderId: folderBusinessEntity.folderId,
          missionId: folderBusinessEntity._id,
          kind: folderBusinessEntity.kind,
          compositeMissionId: folderBusinessEntity.compositeMissionId,
        });
        break;
      }
      case 'BusinessEvent':
        path = buildPath(PATH_BUSINESS_EVENT_DEFAULT, {
          id: folderBusinessEntity._id,
        });
        break;
      default:
        throw new Error('Unknown business entity type');
    }

    void navigate(path);
  }, [navigate, folderBusinessEntity]);

  const actionLabel = 'Consulter la fiche';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  folderBusinessEntityStates: readonly FolderBusinessEntityState[],
): readonly GridColDef<FolderBusinessEntityListCmp_FolderBusinessEntity>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (
      params: Readonly<
        GridRowParams<FolderBusinessEntityListCmp_FolderBusinessEntity>
      >,
    ) => [<Action folderBusinessEntity={params.row} key="consult" />],
  },
  {
    field: 'code',
    headerName: 'Code',
    width: 300,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => ('code' in row ? row.code : null),
  },
  {
    field: 'title',
    headerName: 'Titre',
    width: 400,
    description: 'Titre',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => row.consolidation.title,
  },
  {
    field: 'description',
    headerName: 'Description',
    width: 400,
    description: 'Description',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => row.consolidation.description,
  },
  {
    field: 'emitters',
    headerName: 'Émetteur(s)',
    width: 400,
    description: 'Émetteur(s)',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) =>
      row.consolidation.quotationEmitterActors
        .map(({ thirdParty: pThirdParty }) => {
          const thirdParty = getFragmentData(
            FolderBusinessEntityListCmp_ThirdParty_Fragment,
            pThirdParty,
          );

          switch (thirdParty.__typename) {
            case 'Organization':
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            case ThirdPartyTypeEnum.Organization:
              return thirdParty.name;
            case 'Person':
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            case ThirdPartyTypeEnum.Person:
              return thirdParty.fullName;
            default:
              throw new Error('Unexpected emitter type');
          }
        })
        .join(', '),
  },
  {
    field: 'receivers',
    headerName: 'Destinataire(s)',
    width: 400,
    description: 'Destinataire(s)',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) =>
      row.consolidation.quotationReceiverActors
        .map(({ thirdParty: pThirdParty }) => {
          const thirdParty = getFragmentData(
            FolderBusinessEntityListCmp_ThirdParty_Fragment,
            pThirdParty,
          );

          switch (thirdParty.__typename) {
            case 'Organization':
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            case ThirdPartyTypeEnum.Organization:
              return thirdParty.name;
            case 'Person':
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            case ThirdPartyTypeEnum.Person:
              return thirdParty.fullName;
            default:
              throw new Error('Unexpected receiver type');
          }
        })
        .join(', '),
  },
  {
    field: 'address',
    headerName: 'Adresse',
    width: 400,
    description: 'Adresse',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => {
      const { postalInfo } = getFragmentData(
        AddressCardCmp_Address_Fragment,
        row.consolidation.address,
      );

      if (postalInfo) {
        return [
          `${postalInfo.streetNumber} ${postalInfo.route}`,
          postalInfo.additional,
          postalInfo.building,
        ]
          .filter(BooleanFilter)
          .join(', ');
      }

      return null;
    },
  },
  {
    field: 'locality',
    headerName: 'Commune',
    width: 300,
    description: 'Commune',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => {
      const { postalInfo } = getFragmentData(
        AddressCardCmp_Address_Fragment,
        row.consolidation.address,
      );

      if (postalInfo) {
        return [postalInfo.postalCode, postalInfo.locality]
          .filter(BooleanFilter)
          .join(', ');
      }

      return null;
    },
  },
  {
    field: 'department',
    headerName: 'Département',
    width: 300,
    description: 'Département',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => {
      const { postalInfo } = getFragmentData(
        AddressCardCmp_Address_Fragment,
        row.consolidation.address,
      );

      return postalInfo?.department;
    },
  },
  {
    field: 'region',
    headerName: 'Région',
    width: 300,
    description: 'Région',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => {
      const { postalInfo } = getFragmentData(
        AddressCardCmp_Address_Fragment,
        row.consolidation.address,
      );

      return postalInfo?.region;
    },
  },
  {
    field: 'country',
    headerName: 'Pays',
    width: 100,
    description: 'Pays',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => {
      const { postalInfo } = getFragmentData(
        AddressCardCmp_Address_Fragment,
        row.consolidation.address,
      );

      return postalInfo?.countryAlpha2;
    },
  },
  {
    field: 'coordinates',
    headerName: 'Coordonnées',
    width: 300,
    description: 'Coordonnées',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => {
      const { locationInfo } = getFragmentData(
        AddressCardCmp_Address_Fragment,
        row.consolidation.address,
      );

      return locationInfo?.coordinates.join(', ');
    },
  },
  {
    field: 'cadastralInfo',
    headerName: 'Cadastre',
    width: 300,
    description: 'Cadastre',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => {
      const { cadastralInfo } = getFragmentData(
        AddressCardCmp_Address_Fragment,
        row.consolidation.address,
      );

      return [
        cadastralInfo?.departmentCode,
        cadastralInfo?.districtCode,
        cadastralInfo?.municipalityCode,
        cadastralInfo?.number,
        cadastralInfo?.oldMunicipalityCode,
        cadastralInfo?.section,
        cadastralInfo?.sheet,
      ]
        .filter(BooleanFilter)
        .join(', ');
    },
  },
  {
    field: nameof<FolderBusinessEntityListCmp_FolderBusinessEntity>(
      'businessCreationDate',
    ),
    headerName: 'Date de création',
    width: 200,
    description: 'Date de création',
    editable: false,
    sortable: true,
    type: 'dateTime',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) => (row.businessCreationDate ? new Date(row.businessCreationDate) : null),
  },
  {
    field: 'startDateForAnalytics',
    headerName: 'Date de début',
    width: 200,
    description: 'Date de début',
    editable: false,
    sortable: true,
    type: 'dateTime',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) =>
      'archiveInfo' in row && row.archiveInfo?.startDateForAnalytics
        ? new Date(row.archiveInfo.startDateForAnalytics)
        : null,
  },
  {
    field: 'endDateForAnalytics',
    headerName: 'Date de fin',
    width: 200,
    description: 'Date de fin',
    editable: false,
    sortable: true,
    type: 'dateTime',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) =>
      'archiveInfo' in row && row.archiveInfo?.endDateForAnalytics
        ? new Date(row.archiveInfo.endDateForAnalytics)
        : null,
  },
  {
    field: nameof<FolderBusinessEntityListCmp_FolderBusinessEntity>('stateId'),
    headerName: 'Statut',
    width: 200,
    description: 'Statut',
    editable: false,
    sortable: true,
    type: 'singleSelect',
    valueGetter: (
      _: unknown,
      row: Readonly<FolderBusinessEntityListCmp_FolderBusinessEntity>,
    ) =>
      folderBusinessEntityStates.find((state) => state._id === row.stateId)
        ?.code ?? 'Inconnu',
    valueOptions: folderBusinessEntityStates.map(
      (folderBusinessEntityState) => ({
        value: folderBusinessEntityState.code,
        label: folderBusinessEntityState.label,
      }),
    ),
  },
  {
    field:
      nameof<FolderBusinessEntityListCmp_FolderBusinessEntity>(
        'isConfidential',
      ),
    headerName: 'Confidentiel',
    width: 100,
    description: 'Confidentiel',
    editable: false,
    sortable: true,
    type: 'boolean',
  },
];
